import { queryOptions, type QueryClient } from '@tanstack/react-query'

import { fetchSession, type Session } from '#admin/features/auth/session.ts'

export const sessionQuery = () =>
  queryOptions({
    queryKey: ['auth', 'session'],
    queryFn: ({ signal }) => fetchSession(signal),
    // The session outlives a navigation; refetching it per route would put a request in front
    // of every page for an answer that changes when somebody signs out and not before.
    staleTime: 60_000,
  })

/**
 * Read the session **fresh**, and wait for it — the step between "the password was accepted" and
 * "go to the dashboard".
 *
 * The sign-in page used to *invalidate* the session and navigate without waiting. But anybody who
 * reached `/login` by being redirected there already has `{ user: null }` in the cache — the
 * protected layout's own check put it there — and `ensureQueryData` in that layout's loader
 * returns cached data, stale or not. So the navigation read the old answer, saw nobody signed in,
 * and sent them straight back to the login page they had just successfully used. Fetching with
 * `staleTime: 0` and awaiting it replaces the cached answer before anything reads it.
 */
export function establishSession(queryClient: QueryClient): Promise<Session> {
  return queryClient.fetchQuery({ ...sessionQuery(), staleTime: 0 })
}
